"use client"

// =====================================================
// BOOTH — PostHog client integration
// =====================================================
// Three pieces live here:
//
//   1. PostHogProvider     — initializes posthog-js once on mount and
//                            exposes the client via posthog-js/react context.
//   2. PostHogPageView     — fires a manual $pageview on every App Router
//                            navigation (Next's client router does NOT emit
//                            page loads on soft navigations, so autocapture
//                            of pageviews is disabled in init below).
//   3. PostHogIdentify      — ties analytics to the signed-in Supabase user
//                            (id, email, role) by reading UserRoleProvider,
//                            and calls reset() on logout.
//   4. PostHogRecordingGuard — stops session replay on routes that render
//                            other people's private text.
//
// Ingestion is reverse-proxied through /ingest (see next.config.mjs
// rewrites) so ad/tracker blockers don't drop events. The browser only ever
// talks to our own origin; PostHog never appears in the network tab.
// =====================================================

import { useEffect, useRef, Suspense } from "react"
import posthog from "posthog-js"
import { PostHogProvider as PHProvider, usePostHog } from "posthog-js/react"
import { usePathname, useSearchParams } from "next/navigation"
import { useUserRole } from "@/providers/user-role-provider"

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    const key = process.env.NEXT_PUBLIC_POSTHOG_KEY
    // No key configured (e.g. local dev without secrets) → no-op. The app
    // renders normally; analytics is simply disabled.
    if (!key) return
    if (posthog.__loaded) return

    posthog.init(key, {
      // Reverse proxy — same-origin path defined in next.config.mjs.
      api_host: "/ingest",
      // The real PostHog host, used for the toolbar / feature-flag UI links.
      ui_host: process.env.NEXT_PUBLIC_POSTHOG_HOST || "https://us.posthog.com",
      // We emit pageviews manually (see PostHogPageView) so SPA navigations
      // are tracked; autocapture of $pageview would only fire on hard loads.
      capture_pageview: false,
      capture_pageleave: true,
      // Let the SDK register its own unhandled error/rejection listeners.
      capture_exceptions: true,
      // Only create person profiles for users we identify() — keeps anonymous
      // marketing traffic from inflating MTU billing.
      person_profiles: "identified_only",
      // Mask user input in session replays by default; we surface emails via
      // identify() properties, not by recording them on screen.
      session_recording: {
        maskAllInputs: true,
      },
    })
  }, [])

  return <PHProvider client={posthog}>{children}</PHProvider>
}

// --- Pageview tracking -------------------------------------------------------

// Query parameters that must never reach an analytics event.
//
// $current_url is sent on every pageview, so whatever is in the address bar is
// in PostHog — this is a separate channel from session replay, and blocking
// replay on a route does nothing about it. /auth/verify?email= is the live
// case: a real address, on a page every new signup lands on.
//
// Supabase puts recovery and magic-link tokens in the HASH fragment, which
// useSearchParams never sees and this never built into the URL. The token
// names below are belt and braces for any flow that moves to the query later.
const SCRUBBED_PARAMS = new Set([
  "email",
  "token",
  "token_hash",
  "code",
  "access_token",
  "refresh_token",
])

/** Rebuild the query string with sensitive values replaced, not dropped —
 *  keeping the key means a funnel can still tell "arrived with an email" from
 *  "arrived without one". */
function scrubQuery(searchParams: ReturnType<typeof useSearchParams>): string {
  if (!searchParams) return ""
  let dirty = false
  const out = new URLSearchParams()
  searchParams.forEach((value, key) => {
    if (SCRUBBED_PARAMS.has(key.toLowerCase())) {
      out.set(key, "redacted")
      dirty = true
    } else {
      out.set(key, value)
    }
  })
  return dirty ? out.toString() : searchParams.toString()
}

function PostHogPageViewInner() {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const posthog = usePostHog()

  useEffect(() => {
    if (!pathname || !posthog) return
    let url = window.origin + pathname
    const qs = scrubQuery(searchParams)
    if (qs) url += `?${qs}`
    posthog.capture("$pageview", { $current_url: url })
  }, [pathname, searchParams, posthog])

  return null
}

// useSearchParams() opts the subtree into client rendering, so it must sit
// behind a Suspense boundary to avoid de-opting the whole route.
export function PostHogPageView() {
  return (
    <Suspense fallback={null}>
      <PostHogPageViewInner />
    </Suspense>
  )
}

// --- Session replay route guard ----------------------------------------------
//
// maskAllInputs in init() masks what a user TYPES. It does nothing about text
// the app RENDERS — a message thread, a rent figure, a client's name and phone
// are all plain DOM, and replay captured every one of them. The person on
// screen never agreed to that, and often is not even the person being recorded:
// a replay of an owner reading their inbox is a recording of the barber who
// wrote to them.
//
// PostHog's own answer is a URL blocklist in project settings. That works, but
// it lives in a dashboard where nothing in this repo can see it, review it, or
// tell you it was switched off. This is the same rule, in the file that already
// decides what we collect, shipped and reverted by deploy like everything else.
//
// Replay stays on everywhere else: marketing pages, browse, onboarding, the
// funnels this is actually for.

const REPLAY_BLOCKED = [
  // Private correspondence between two people.
  "/messages",
  // Money: what someone pays, is owed, or has been charged.
  "/payments",
  "/scheduled-payments",
  "/billing",
  // Signed agreements — names, addresses, rent, signatures.
  "/contracts",
  "/documents",
  // The account holder's own details.
  "/account",
  "/settings",
  // A barber's client list and a client's own screens.
  "/clients",
  "/dashboard/client",
  // Notification bodies quote messages and amounts verbatim.
  "/notifications",
  // These carry a single-use token in the URL, and replay records the URL.
  // Masking inputs does not help when the secret is in the address bar.
  "/auth/reset-password",
  "/auth/verify",
  "/auth/mfa-verify",
  "/auth/2fa",
]

const isReplayBlocked = (pathname: string) =>
  REPLAY_BLOCKED.some((seg) => pathname.includes(seg))

function PostHogRecordingGuardInner() {
  const pathname = usePathname()
  // Only resume what we ourselves paused. If replay is off for some other
  // reason — sampling, a killswitch, project settings — calling start() here
  // would quietly override that decision.
  const pausedByUs = useRef(false)

  useEffect(() => {
    if (!pathname || !posthog.__loaded) return

    if (isReplayBlocked(pathname)) {
      if (!pausedByUs.current) {
        posthog.stopSessionRecording()
        pausedByUs.current = true
      }
      return
    }

    if (pausedByUs.current) {
      posthog.startSessionRecording()
      pausedByUs.current = false
    }
  }, [pathname])

  return null
}

// usePathname is fine outside Suspense, but this sits next to PostHogPageView
// which is not — keeping the same shape avoids one being moved without the other.
export function PostHogRecordingGuard() {
  return (
    <Suspense fallback={null}>
      <PostHogRecordingGuardInner />
    </Suspense>
  )
}

// --- User identification -----------------------------------------------------

export function PostHogIdentify() {
  const posthog = usePostHog()
  const { userId, profile, role } = useUserRole()
  const identifiedId = useRef<string | null>(null)

  useEffect(() => {
    if (!posthog) return

    if (userId && profile) {
      // Re-identifying with the same id just refreshes properties — cheap.
      posthog.identify(userId, {
        email: profile.email,
        name: profile.full_name || profile.first_name,
        // The user's real account type from their profile row…
        account_type: profile.role,
        // …vs. the role they're currently browsing as (barbers can toggle).
        active_role: role,
      })
      identifiedId.current = userId
    } else if (!userId && identifiedId.current) {
      // Logged out: drop the identity so the next anon session is clean.
      posthog.reset()
      identifiedId.current = null
    }
  }, [posthog, userId, profile, role])

  return null
}
